import type { DieFace, RollResult, PointNumber } from './types.js';
import { defaultRng, type RNG } from '@casino/core';

export interface DiceSource {
  roll(): { d1: DieFace; d2: DieFace };
}

export class FairDice implements DiceSource {
  private rng: RNG;
  constructor(rng: RNG = defaultRng()) {
    this.rng = rng;
  }
  roll(): { d1: DieFace; d2: DieFace } {
    return {
      d1: (Math.floor(this.rng.next() * 6) + 1) as DieFace,
      d2: (Math.floor(this.rng.next() * 6) + 1) as DieFace,
    };
  }
}

export function makeRollResult(
  d1: DieFace,
  d2: DieFace,
  currentPoint: PointNumber | undefined,
): RollResult {
  const total = d1 + d2;
  return {
    d1,
    d2,
    total,
    isHardway: d1 === d2 && (total === 4 || total === 6 || total === 8 || total === 10),
    isCraps: total === 2 || total === 3 || total === 12,
    isNatural: total === 7 || total === 11,
    isPoint: currentPoint !== undefined && total === currentPoint,
    isSeven: total === 7,
  };
}

export class WeightedDice implements DiceSource {
  private rng: RNG;
  private d1Cdf: number[];
  private d2Cdf: number[];

  constructor(d1Weights: readonly number[], d2Weights: readonly number[], rng: RNG = defaultRng()) {
    this.rng = rng;
    this.d1Cdf = WeightedDice.makeCdf(d1Weights);
    this.d2Cdf = WeightedDice.makeCdf(d2Weights);
  }

  private static makeCdf(weights: readonly number[]): number[] {
    if (weights.length !== 6) throw new Error('weights must have length 6');
    if (weights.some((w) => w < 0 || !Number.isFinite(w))) {
      throw new Error('weights must be non-negative finite numbers');
    }
    const total = weights.reduce((a, b) => a + b, 0);
    if (total <= 0) throw new Error('weights must sum to a positive value');
    const cdf: number[] = [];
    let acc = 0;
    for (const w of weights) {
      acc += w / total;
      cdf.push(acc);
    }
    return cdf;
  }

  private sample(cdf: number[]): DieFace {
    const r = this.rng.next();
    for (let i = 0; i < 6; i++) {
      const c = cdf[i];
      if (c !== undefined && r < c) return (i + 1) as DieFace;
    }
    return 6;
  }

  roll(): { d1: DieFace; d2: DieFace } {
    return { d1: this.sample(this.d1Cdf), d2: this.sample(this.d2Cdf) };
  }
}
